import { useState, useEffect } from 'react';
import Section from '@widgets/components/section';
import { TextInput } from '@shared/uikit/input-text';
import { Loader } from '@shared/uikit/loader';
import { toast } from 'react-toastify';
import { Pencil } from 'lucide-react';
import BaseButton from '@shared/uikit/base-button';
import { TextSize, Typography } from '@shared/index';
import { formatBtc } from '@app/utils/math';
import {
  useCreateManagerSalary,
  useUpdateManagerSalary,
} from '@entities/managers-perfomance';

type Props = {
  managerId: number | null;
  month?: number;
  year?: number;
  managerSalary?: {
    id?: number | null;
    salary?: number;
    bonus?: number;
    penalty?: number;
    addinional_bonus?: number;
    flex?: number;
    total?: number;
  } | null;
  refetchSalary?: () => void;
};

export const SalaryCalculation = ({
  managerId,
  month,
  year,
  managerSalary,
  refetchSalary,
}: Props) => {
  const currentYear = year ?? new Date().getFullYear();

  const createManagerSalary = useCreateManagerSalary();
  const updateManagerSalary = useUpdateManagerSalary();

  const [isEditing, setEditing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [salary, setSalary] = useState<string>('');
  const [penalty, setPenalty] = useState<string>('');
  const [bonus, setBonus] = useState<string>('');
  const [additionalBonus, setAdditionalBonus] = useState<string>('');

  useEffect(() => {
    if (!managerSalary) return;

    setSalary(managerSalary.salary?.toString() ?? '');
    setPenalty(managerSalary.penalty?.toString() ?? '');
    setBonus(managerSalary.bonus?.toString() ?? '');
    setAdditionalBonus(managerSalary.addinional_bonus?.toString() ?? '');
  }, [managerSalary]);

  const handleUpdateSalary = async () => {
    if (managerId == null || month == null) {
      toast.error('Некорректные данные');
      return;
    }

    setIsSubmitting(true);

    try {
      if (!managerSalary?.id) {
        await createManagerSalary.fetch({
          managerId,
          year: currentYear,
          month,
          salary,
          bonus,
          additionalBonus,
          penalty,
        });

        toast.success('Данные успешно добавлены');
      } else {
        await updateManagerSalary.fetch({
          salaryId: managerSalary.id,
          managerId,
          year: currentYear,
          month,
          salary,
          bonus,
          additionalBonus,
          penalty,
        });

        toast.success('Данные успешно обновлены');
      }

      await refetchSalary?.();
      setEditing(false);
    } catch (error) {
      console.error('Error updating salary:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSum = (() => {
    const parts = [];

    const usdtPart =
      Number(managerSalary?.salary ?? 0) +
      Number(managerSalary?.bonus ?? 0) -
      Number(managerSalary?.penalty ?? 0) +
      Number(managerSalary?.addinional_bonus ?? 0);
    parts.push(`${formatBtc(usdtPart, 2)} USDT`);

    const flexVal = Number(managerSalary?.flex ?? 0);
    parts.push(`${formatBtc(flexVal)} BTC`);

    return parts.join(' + ');
  })();

  return (
    <Section>
      <div className="flex items-center justify-between text-[20px] mb-[20px]">
        <span className="font-semibold text-[20px]">Расчёт</span>
        <button
          className="w-[20px] h-[20px]"
          onClick={() => {
            setEditing(true);
          }}
        >
          <Pencil size={16} className="text-gray-400" />
        </button>
      </div>
      <div className="grid md:grid-cols-4 gap-8">
        <div className="w-full">
          <div className="mb-[10px] text-[#959595]">Фикс</div>
          <div
            style={{
              backgroundColor: isEditing ? '#211F24' : '#141316',
            }}
            className={`h-[39px] w-full rounded-[8px] text-[12px] px-[10px] flex items-center border border-solid border-[#2B292F]`}
          >
            {isEditing ? (
              <div className="w-full flex items-center text-[16px]">
                <TextInput
                  width="100%"
                  value={`${salary}`}
                  bgColor="#211F24"
                  borderColor="#211F24"
                  onChange={(value: string) => setSalary(value)}
                  decimalFormattingMode={true}
                />
              </div>
            ) : (
              <span className="text-[16px]">$ {managerSalary?.salary ?? '-'}</span>
            )}
          </div>
        </div>
        <div className="w-full">
          <div className="mb-[10px] text-[#959595]">Флекс</div>
          <div
            className={`h-[39px] w-full rounded-[8px] text-[12px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
          >
            <span className="text-[16px]">
              {managerSalary?.flex != null ? `${formatBtc(managerSalary.flex)} BTC` : '-'}
            </span>
          </div>
        </div>
        <div className="w-full">
          <div className="mb-[10px] text-[#959595]">Штраф</div>
          <div
            style={{
              backgroundColor: isEditing ? '#211F24' : '#141316',
            }}
            className={`h-[39px] w-full rounded-[8px] text-[12px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
          >
            {isEditing ? (
              <div className="w-full flex items-center text-[16px]">
                <TextInput
                  width="100%"
                  value={`${penalty}`}
                  bgColor="#211F24"
                  borderColor="#211F24"
                  onChange={(value: string) => setPenalty(value)}
                  decimalFormattingMode={true}
                />
              </div>
            ) : (
              <span className="text-[16px]">{managerSalary?.penalty ?? '-'}</span>
            )}
          </div>
        </div>
        <div className="w-full">
          <div className="mb-[10px] text-[#959595]">Доп. продажи</div>
          <div
            style={{
              backgroundColor: isEditing ? '#211F24' : '#141316',
            }}
            className={`h-[39px] w-full rounded-[8px] text-[12px] px-[10px] flex items-center border border-solid border-[#2B292F]`}
          >
            {isEditing ? (
              <div className="w-full flex items-center text-[16px]">
                <TextInput
                  width="100%"
                  value={`${additionalBonus}`}
                  bgColor="#211F24"
                  borderColor="#211F24"
                  onChange={(value: string) => setAdditionalBonus(value)}
                  decimalFormattingMode={true}
                />
              </div>
            ) : (
              <span className="text-[16px]">{managerSalary?.addinional_bonus ?? '-'}</span>
            )}
          </div>
        </div>
        <div className="flex w-full justify-between items-end col-span-4">
          <div className="flex flex-col">
            <Typography
              text="Итого к выплате"
              size={TextSize.size14}
              className="mb-[10px] font-medium text-[#959595]"
            />

            <div
              className="h-[39px] w-full min-w-[291px] rounded-[8px] text-[12px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]"
            >
              <span className="text-[16px]">
                {totalSum}
              </span>
            </div>
          </div>
          <div className="flex items-end gap-x-[10px]">
            <BaseButton
              className="p-[10px] w-auto h-auto border border-[#383838] min-w-[100px]"
              onClick={() => setEditing(false)}
            >
              <Typography
                text="Отменить"
                size={TextSize.size14}
                className="font-medium text-white/60"
              />
            </BaseButton>
            <BaseButton
              className="p-[10px] w-auto h-auto border-none bg-[#3A48DF] min-w-[174px]"
              onClick={handleUpdateSalary}
            >
              {isSubmitting ? (
                <Loader size={24} position="center" loading />
              ) : (
                <Typography
                  text="Сохранить изменения"
                  size={TextSize.size14}
                  className="font-medium text-white"
                />
              )}
            </BaseButton>
          </div>
        </div>
      </div>
    </Section>
  );
};
